import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { GenericLayer } from "src/types";
import { layerName } from "src/utils/overlayer.utils";

interface Props<T> {
  layers: T[];
  hasNoneOption?: boolean;
  onChange: (layer: T | null) => void;
}

export function LayerGroupRadio<T extends GenericLayer>(props: Props<T>) {
  const { layers, hasNoneOption = false } = props;
  const { t } = useTranslation();

  const activeLayer = useMemo(() => layers.find((l) => l.isActive), [layers]);

  function handleChange(key: GenericLayer["key"]) {
    const layer = layers.find((l) => l.key === key);
    if (layer) props.onChange(layer);
    else props.onChange(null);
  }

  return render();

  function render() {
    return (
      <StyledRadioGroup
        value={activeLayer?.key || ""}
        onChange={(event, value) => handleChange(value)}
        className="radio-group"
      >
        {hasNoneOption && (
          <FormControlLabel
            className="layer-item"
            value=""
            control={<Radio color="primary" />}
            label={<div className="overflow-box">{t("label.none")}</div>}
          />
        )}

        {layers.map((layer: T) => {
          return (
            <FormControlLabel
              key={layer.key}
              className="layer-item"
              value={layer.key}
              control={<Radio color="primary" />}
              label={<div className="overflow-box">{layerName(layer, t)}</div>}
            />
          );
        })}
      </StyledRadioGroup>
    );
  }
}

const StyledRadioGroup = styled(RadioGroup)`
  padding: ${({ theme }) => theme.spacing(0, 2)};

  .MuiFormLabel-root,
  .MuiFormControlLabel-label {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
  }
  .layer-item {
    user-select: none;
    height: 50px;
  }

  --overflow-box-width: calc(var(--layer-manager-width) - 70px);

  .overflow-box {
    width: var(--overflow-box-width);
    height: 30px;
    overflow: hidden;
    line-height: 30px;
    padding: 0px;
    position: relative;
    box-sizing: border-box;
  }

  .overflow-box span {
    position: absolute;
    white-space: nowrap;
    transform: translateX(0);
    transition: 1s;
    min-width: 100%;
  }
  .overflow-box:hover span {
    transform: translateX(calc(var(--overflow-box-width) - 100%));
  }
`;
